import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  deleteDoc, 
  doc, 
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { deleteEvaluationCodeLookup, upsertEvaluationCodeLookup } from '../../utils/arcadeGuestUpload';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import { format } from 'date-fns';
import { FirebaseError } from 'firebase/app';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import InputField from '../../components/ui/InputField';
import TrashIcon from '../../components/icons/TrashIcon';

interface EvaluationCode {
  id: string;
  code: string;
  schoolId: string;
  schoolName: string;
  grade: number;
  startDate: Date;
  endDate: Date;
  reviewerClasses: {[key: string]: string}; // 평가하는 반 -> 평가받는 반
  createdAt: Date;
  createdBy: string;
}

const EvaluationCodeGenerator: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [evaluationCodes, setEvaluationCodes] = useState<EvaluationCode[]>([]);
  const [schoolName, setSchoolName] = useState<string>('');
  const [maxClassCount, setMaxClassCount] = useState<number>(0);
  const [gradeClassCounts, setGradeClassCounts] = useState<{[key: number]: number}>({});
  
  // 평가 코드 생성 폼 상태
  const [grade, setGrade] = useState<number>(1);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date(new Date().setDate(new Date().getDate() + 7)));
  
  // 학급 선택 다이얼로그 상태 제거
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [selectedReviewerClass, setSelectedReviewerClass] = useState<string | null>(null);
  const [selectedTargetClass, setSelectedTargetClass] = useState<string | null>(null);
  
  // 평가 가능한 반 선택 상태
  const [classPermissions, setClassPermissions] = useState<{[key: string]: string[]}>({});
  
  // 학급 목록 생성 (선택된 학년의 반 수에 따라)
  const classList = Array.from({ length: gradeClassCounts[grade] || 0 }, (_, i) => (i + 1).toString());
  
  useEffect(() => {
    // 교사 권한 확인
    if (!currentUser || currentUser.role !== 'teacher' && currentUser.role !== 'superAdmin' && currentUser.role !== 'schoolAdmin') {
      navigate('/makecodearcade');
      return;
    }
    
    fetchSchoolInfo();
    fetchEvaluationCodes();
  }, [currentUser, navigate]);
  
  // 학년이 변경될 때마다 평가 권한 초기화
  useEffect(() => {
    setClassPermissions({});
  }, [grade]);
  
  const fetchSchoolInfo = async () => {
    if (!currentUser || !currentUser.schoolId) return;
    
    try {
      const schoolDoc = await getDoc(doc(db, 'schools', currentUser.schoolId));
      if (schoolDoc.exists()) {
        const schoolData = schoolDoc.data();
        setSchoolName(schoolData.name || '학교명 없음');
        
        // gradeClasses 필드에서 학년별 반 수 설정
        const gradeClasses = schoolData.gradeClasses || {};
        setGradeClassCounts(gradeClasses);
        
        // 현재 선택된 학년의 반 수로 초기화
        setMaxClassCount(gradeClasses[grade] || 0);
      } else {
        setSchoolName('학교명 없음');
        setGradeClassCounts({});
        setMaxClassCount(0);
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
      setSchoolName('학교명 없음');
      setGradeClassCounts({});
      setMaxClassCount(0);
    }
  };
  
  const handleGradeChange = (newGrade: number) => {
    setGrade(newGrade);
    setMaxClassCount(gradeClassCounts[newGrade] || 0);
  };
  
  const fetchEvaluationCodes = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      console.log('Fetching evaluation codes for schoolId:', currentUser.schoolId);
      console.log('Current user role:', currentUser.role);
      
      const codesRef = collection(db, 'evaluationCodes');
      const q = query(
        codesRef, 
        where('schoolId', '==', currentUser.schoolId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      const codes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate(),
        createdAt: doc.data().createdAt.toDate()
      })) as EvaluationCode[];
      
      setEvaluationCodes(codes);
      await Promise.all(codes.map((code) => upsertEvaluationCodeLookup({
        code: code.code,
        schoolId: code.schoolId,
        schoolName: code.schoolName,
        grade: code.grade,
        startDate: code.startDate,
        endDate: code.endDate,
        reviewerClasses: code.reviewerClasses,
        createdBy: code.createdBy,
      }).catch((lookupError) => {
        console.error('평가 코드 공개 조회본 저장 실패:', lookupError);
      })));
    } catch (error) {
      console.error('Error fetching evaluation codes:', error);
      
      // 오류 메시지 상세화
      if (error instanceof FirebaseError) {
        console.error('Firebase error code:', error.code);
        console.error('Firebase error message:', error.message);
        
        if (error.code === 'permission-denied') {
          setError('평가 코드 목록에 접근할 권한이 없습니다. 교사 권한이 필요합니다.');
        } else {
          setError(`평가 코드 목록을 불러오는 중 오류가 발생했습니다: ${error.message}`);
        }
      } else {
        setError('평가 코드 목록을 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };
  
  const handleClassPermissionChange = (reviewerClass: string, targetClass: string, checked: boolean) => {
    setClassPermissions(prev => {
      const currentTargets = prev[reviewerClass] || [];
      if (checked) {
        return {
          ...prev,
          [reviewerClass]: [...currentTargets, targetClass]
        };
      } else {
        return {
          ...prev,
          [reviewerClass]: currentTargets.filter(c => c !== targetClass)
        };
      }
    });
  };
  
  const handleGenerateCode = async () => {
    if (!currentUser || currentUser.role !== 'teacher' && currentUser.role !== 'superAdmin' && currentUser.role !== 'schoolAdmin') {
      setError('교사 권한이 필요합니다.');
      return;
    }
    
    if (!startDate || !endDate) {
      setError('평가 기간을 설정해주세요.');
      return;
    }
    
    // 평가 권한이 하나도 없는 경우 체크
    const hasAnyPermissions = Object.values(classPermissions).some(targets => targets.length > 0);
    if (!hasAnyPermissions) {
      setError('최소 하나 이상의 평가 권한을 설정해주세요.');
      return;
    }
    
    if (startDate > endDate) {
      setError('시작일은 종료일보다 이전이어야 합니다.');
      return;
    }
    
    try {
      const code = generateRandomCode();
      
      // reviewerClasses 형식으로 변환
      const reviewerClasses: {[key: string]: string} = {};
      Object.entries(classPermissions).forEach(([reviewerClass, targetClasses]) => {
        targetClasses.forEach(targetClass => {
          reviewerClasses[reviewerClass] = targetClass;
        });
      });
      
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(endDate);
      await addDoc(collection(db, 'evaluationCodes'), {
        code,
        schoolId: currentUser.schoolId,
        schoolName: schoolName,
        grade,
        startDate: startTimestamp,
        endDate: endTimestamp,
        reviewerClasses,
        createdAt: Timestamp.fromDate(new Date()),
        createdBy: currentUser.uid
      });
      await upsertEvaluationCodeLookup({
        code,
        schoolId: currentUser.schoolId,
        schoolName,
        grade,
        startDate: startTimestamp,
        endDate: endTimestamp,
        reviewerClasses,
        createdBy: currentUser.uid,
      });
      
      setSuccess('평가 코드가 생성되었습니다.');
      fetchEvaluationCodes();
      
      // 폼 초기화
      setGrade(1);
      setStartDate(new Date());
      setEndDate(new Date(new Date().setDate(new Date().getDate() + 7)));
      setClassPermissions({});
    } catch (error) {
      console.error('Error generating evaluation code:', error);
      setError('평가 코드 생성 중 오류가 발생했습니다.');
    }
  };
  
  const handleDeleteCode = async (codeId: string, codeValue: string) => {
    try {
      await deleteDoc(doc(db, 'evaluationCodes', codeId));
      await deleteEvaluationCodeLookup(codeValue).catch((lookupError) => {
        console.error('평가 코드 공개 조회본 삭제 실패:', lookupError);
      });
      setSuccess('평가 코드가 삭제되었습니다.');
      fetchEvaluationCodes();
    } catch (error) {
      console.error('Error deleting evaluation code:', error);
      setError('평가 코드 삭제 중 오류가 발생했습니다.');
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('클립보드에 복사되었습니다.');
  };
  
  if (loading) {
    return (
      <MainLayout>
        <div style={getMakecodeArcadePageRootStyle(isMobile, { display: 'flex', justifyContent: 'center' })}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #FF6F0F', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      </MainLayout>
    );
  }
  
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;
    return new Date(dateString);
  };
  
  return (
    <MainLayout>
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 600, 
          marginBottom: '16px',
          color: '#161616',
          fontFamily: 'Pretendard'
        }}>
          평가 코드 생성
        </h1>
        
        <Card padding="20px" style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: 600, 
            marginBottom: '24px',
            color: '#161616',
            fontFamily: 'Pretendard'
          }}>
            새 평가 코드 생성
          </h2>
            
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '200px', flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: 500,
                  color: '#161616',
                  fontFamily: 'Pretendard'
                }}>
                  학년
                </label>
                <select
                  value={grade}
                  onChange={(e) => handleGradeChange(Number(e.target.value))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {Object.keys(gradeClassCounts).map((g) => (
                    <option key={g} value={Number(g)}>
                      {g}학년 ({gradeClassCounts[Number(g)]}반)
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ minWidth: '200px', flex: 1 }}>
                <InputField
                  label="시작일"
                  type="date"
                  value={formatDateForInput(startDate)}
                  onChange={(value) => setStartDate(parseDateFromInput(value))}
                />
              </div>
            </div>
              
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '200px', flex: 1 }}>
                <InputField
                  label="종료일"
                  type="date"
                  value={formatDateForInput(endDate)}
                  onChange={(value) => setEndDate(parseDateFromInput(value))}
                />
              </div>
            </div>
              
            <div>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                marginBottom: '16px',
                color: '#161616',
                fontFamily: 'Pretendard'
              }}>
                평가 권한 설정
              </h3>
              
              {maxClassCount === 0 ? (
                <div style={{ 
                  padding: '12px 16px', 
                  backgroundColor: '#FFF3E0', 
                  color: '#F57C00', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard'
                }}>
                  {grade}학년의 반 정보가 없습니다.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {classList.map((reviewerClass) => (
                    <Card key={reviewerClass} padding="16px" style={{ border: '1px solid #F0F0F0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ 
                          minWidth: '120px', 
                          padding: '8px', 
                          backgroundColor: '#E3F2FD', 
                          color: '#1976D2',
                          borderRadius: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Pretendard' }}>
                            {reviewerClass}반
                          </div>
                          <div style={{ fontSize: '12px', fontFamily: 'Pretendard' }}>
                            평가받는 반
                          </div>
                        </div>
                        
                        <span style={{ fontSize: '14px', color: '#989898' }}>→</span>
                        
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: 1 }}>
                          {classList.map((targetClass) => {
                            const isSelected = classPermissions[reviewerClass]?.includes(targetClass);
                            return (
                              <button
                                key={targetClass}
                                onClick={() => {
                                  handleClassPermissionChange(reviewerClass, targetClass, !isSelected);
                                }}
                                style={{
                                  padding: '8px 16px',
                                  border: `1px solid ${isSelected ? '#FF6F0F' : '#F0F0F0'}`,
                                  borderRadius: '8px',
                                  backgroundColor: isSelected ? '#FF6F0F' : 'white',
                                  color: isSelected ? 'white' : '#161616',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontFamily: 'Pretendard',
                                  fontWeight: isSelected ? 600 : 400,
                                  transition: 'all 0.2s'
                                }}
                              >
                                {targetClass}반
                              </button>
                            );
                          })}
                        </div>
                        
                        <span style={{ fontSize: '14px', color: '#989898', marginLeft: '8px' }}>
                          (평가하는 반)
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
              
            <Button 
              variant="primary" 
              onClick={handleGenerateCode}
              width="100%"
            >
              평가 코드 생성
            </Button>
          </div>
        </Card>
        
        <h2 style={{ 
          fontSize: '18px', 
          fontWeight: 600, 
          marginBottom: '16px',
          color: '#161616',
          fontFamily: 'Pretendard'
        }}>
          생성된 평가 코드 목록
        </h2>
        
        {evaluationCodes.length === 0 ? (
          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: '#E3F2FD', 
            color: '#1976D2', 
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'Pretendard'
          }}>
            아직 생성된 평가 코드가 없습니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {evaluationCodes.map((code) => (
              <Card key={code.id} padding="16px" style={{ border: '1px solid #F0F0F0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#161616', fontFamily: 'Pretendard' }}>
                        코드: {code.code}
                      </span>
                      <button
                        onClick={() => copyToClipboard(code.code)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="복사"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="#989898"/>
                        </svg>
                      </button>
                    </div>
                    <div style={{ fontSize: '14px', color: '#161616', marginBottom: '8px', fontFamily: 'Pretendard' }}>
                      {code.grade}학년 | 평가 기간: {format(code.startDate, 'yyyy-MM-dd')} ~ {format(code.endDate, 'yyyy-MM-dd')}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Object.entries(code.reviewerClasses).map(([reviewer, target]) => (
                        <span
                          key={reviewer}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #FF6F0F',
                            borderRadius: '16px',
                            fontSize: '12px',
                            color: '#FF6F0F',
                            fontFamily: 'Pretendard',
                            backgroundColor: 'transparent'
                          }}
                        >
                          {reviewer}반 → {target}반
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteCode(code.id, code.code)}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="삭제"
                  >
                    <TrashIcon width={20} height={20} color="#F44336" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
        
        {error && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '16px 24px',
            backgroundColor: '#F44336',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            maxWidth: '400px'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0',
                marginLeft: 'auto'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        {success && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '16px 24px',
            backgroundColor: '#4CAF50',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            maxWidth: '400px'
          }}>
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0',
                marginLeft: 'auto'
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default EvaluationCodeGenerator; 